import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2, EyeOff, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { STATUS, STATUS_KEYS, fmtDate, fmtPhone, waLink, waTemplate } from "@/lib/helpers";
import { WhatsAppIcon } from "@/components/site/WhatsAppFab";
import { StatusBadge, Flags } from "./StatusBadge";

function Related({ id }) {
  const [rel, setRel] = useState(null);
  useEffect(() => {
    api.get(`/admin/appointments/${id}/related`).then((r) => setRel(r.data)).catch(() => setRel(null));
  }, [id]);
  if (!rel) return null;
  return (
    <div className="space-y-3">
      {rel.same_slot.length > 0 && (
        <div className="rounded-xl border-2 border-red-500 bg-red-50 p-3" data-testid="dialog-conflicts">
          <p className="flex items-center gap-2 font-bold text-red-700 text-sm"><AlertTriangle className="w-4 h-4" /> Aynı saatte {rel.same_slot.length} talep daha var</p>
          <ul className="mt-1 text-sm text-red-800">{rel.same_slot.map((o) => <li key={o.id}>{o.request_no} · {o.name} · {o.course_name} · {STATUS[o.status]?.label}</li>)}</ul>
        </div>
      )}
      {rel.phone_history.length > 0 && (
        <div className="rounded-xl bg-sky-50 border border-sky-200 p-3" data-testid="dialog-phone-history">
          <p className="font-bold text-sky-900 text-sm">Bu numaradan geçmiş talepler ({rel.phone_history.length})</p>
          <ul className="mt-1 space-y-1 text-sm">
            {rel.phone_history.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-2">{o.request_no} · {o.date} {o.time} · {o.course_name} <StatusBadge status={o.status} /></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function AppointmentDialog({ appt, academy, onClose, onChanged }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (appt) {
      setDate(appt.date);
      setTime(appt.time);
      setNote(appt.admin_note || "");
    }
  }, [appt]);
  if (!appt) return null;

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/appointments/${appt.id}`, body);
      toast.success(msg);
      onChanged(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const action = async (fn, msg, confirmText) => {
    if (!window.confirm(confirmText)) return;
    try {
      await fn();
      toast.success(msg);
      onChanged(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!appt} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl" data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black text-navy flex flex-wrap items-center gap-3">
            {appt.request_no} <StatusBadge status={appt.status} />
          </DialogTitle>
        </DialogHeader>
        <Flags a={appt} />
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {[["Ad Soyad", appt.name], ["Telefon", fmtPhone(appt.phone)], ["Kurs", appt.course_name],
            ["Zaman", `${fmtDate(appt.date)} · ${appt.time}`],
            ["Öğrenci", `${appt.student_type === "cocuk" ? "Çocuk" : "Yetişkin"}${appt.student_age ? `, ${appt.student_age} yaş` : ""}`],
            ["Not", appt.note || "—"]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-cream p-3"><p className="text-xs font-bold text-slate-500">{k}</p><p className="font-semibold text-slate-800 break-words">{v}</p></div>
          ))}
        </div>
        <Related id={appt.id} key={appt.id + appt.date + appt.time + appt.status} />
        <div>
          <p className="text-sm font-bold text-navy mb-2">Durum</p>
          <div className="flex flex-wrap gap-2">
            {STATUS_KEYS.map((s) => (
              <button key={s} disabled={busy || s === appt.status} onClick={() => patch({ status: s }, `Durum: ${STATUS[s].label}`)} data-testid={`dialog-status-${s}`}
                className={`rounded-full border px-4 py-2 text-sm font-bold transition-opacity ${STATUS[s].badge} ${s === appt.status ? "ring-2 ring-navy" : "hover:opacity-80"} disabled:cursor-default`}>
                {STATUS[s].label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-bold text-navy mb-2">Saat değiştir</p>
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-auto rounded-xl" data-testid="dialog-date-input" />
            <Input type="time" value={time} step={300} onChange={(e) => setTime(e.target.value)} className="w-auto rounded-xl" data-testid="dialog-time-input" />
            <button disabled={busy || (date === appt.date && time === appt.time)} onClick={() => patch({ date, time }, "Randevu zamanı güncellendi")} data-testid="dialog-reschedule-btn"
              className="rounded-full bg-navy px-5 py-2 text-sm font-bold text-white disabled:opacity-40">Zamanı güncelle</button>
          </div>
        </div>
        <div>
          <p className="text-sm font-bold text-navy mb-2">Yönetici notu</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl" data-testid="dialog-admin-note" />
          <button disabled={busy} onClick={() => patch({ admin_note: note }, "Not kaydedildi")} data-testid="dialog-save-note-btn"
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-bej px-4 py-2 text-sm font-bold text-navy hover:bg-bej-dark"><Save className="w-4 h-4" /> Notu kaydet</button>
        </div>
        {appt.history?.length > 0 && (
          <div className="text-xs text-slate-500 space-y-0.5" data-testid="dialog-history">
            {appt.history.map((h, i) => <p key={i}>{new Date(h.at).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul" })} — {h.action}</p>)}
          </div>
        )}
        <div className="flex flex-wrap gap-2 border-t border-bej pt-4">
          {appt.phone && (
            <a href={waLink(appt.phone, waTemplate(appt, academy))} target="_blank" rel="noreferrer" data-testid="dialog-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-white hover:brightness-95">
              <WhatsAppIcon className="w-4 h-4" /> WhatsApp'tan bilgi ver
            </a>
          )}
          <button onClick={() => action(() => api.post(`/admin/appointments/${appt.id}/anonymize`), "Talep anonimleştirildi", "Bu talepteki kişisel veriler kalıcı olarak silinecek. Emin misiniz?")}
            data-testid="dialog-anonymize-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-200">
            <EyeOff className="w-4 h-4" /> Anonimleştir
          </button>
          <button onClick={() => action(() => api.delete(`/admin/appointments/${appt.id}`), "Talep silindi", "Bu talep kalıcı olarak silinecek. Emin misiniz?")}
            data-testid="dialog-delete-btn" className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-4 py-2.5 text-sm font-bold text-rose-700 hover:bg-rose-100">
            <Trash2 className="w-4 h-4" /> Sil
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
